import { signOut } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import React, { useEffect } from 'react'
import {
    BrowserRouter as Router, Redirect, Route,
    Switch
} from 'react-router-dom'

import { LoadingModal } from '../components/molecules/LoadingModal'
import { Navbar } from '../components/molecules/Navbar'
import { NotificationPopUp, Severity } from '../components/molecules/NotificationPopUp'
import { AppContext } from '../services/app-context'
import { useCognito } from '../services/cognito-services'
import { getSelf } from '../services/user-services'
import { notificationChannelName } from '../utils/notification'
import { UserDto } from '../utils/types'
import AppRoute from './AppRoute'
import { ConfirmSignUp } from './ConfirmSignUp/ConfirmSignUp'
import { EditEscapeRoom } from './EditEscapeRoom/EditEscapeRoom'
import { EscapeRooms } from './EscapeRooms/EscapeRooms'
import { FinalizeSignUp } from './FinalizeSignUp/FinalizeSignUp'
import { ForgotPassword } from './ForgotPassword/ForgotPassword'
import { Game } from './Game/Game'
import { GameMonitoring } from './GameMonitoring/GameMonitoring'
import { GameStarter } from './GameStarter/GameStater'
import { LandingPage } from './LandingPage/LandingPage'
import { Linker } from './Linker/Linker'
import { Login } from './Login/Login'
import { Profile } from './Profile/Profile'
import { Puzzles } from './Puzzles/Puzzles'
import { SignUp } from './SignUp/SignUp'
import { Video } from './Video/Video'

export default function RootRouter() {
    const { signedIn } = useCognito()
    const [checkingLogin, setCheckingLogin] = React.useState(false)
    const [busy, setBusy] = React.useState(false)
    const [showNotification, setShowNotification] = React.useState(false)
    const [notificationSeverity, setNotificationSeverity] = React.useState<Severity>('info')
    const [notificationMessage, setNotificationMessage] = React.useState('')
    const [user, setUser] = React.useState<UserDto>()

    useEffect(() => {
        const checkLogin = async () => {
            // Don't check profile if user is on the finalize page
            if (window.location.pathname === '/afslutte') {
                return
            }

            setCheckingLogin(true)
            try {
                const self = await getSelf()
                setUser(self)
            } catch (error: any) {
                // eslint-disable-next-line no-console
                console.warn('getSelf failed:', error)
                const status = error?.response?.status
                if (status === 403 || status === 404) {
                    window.location.href = '/afslutte'
                    return
                }
                // Other errors → sign out
                try {
                    await signOut()
                } catch (x) {
                    // ignore
                }
            }

            setCheckingLogin(false)
        }

        if (signedIn) {
            checkLogin().then()
        }
    }, [signedIn])

    const notificationListener = React.useCallback((packet) => {

        const event = packet.payload?.event as string | undefined
        if (event === 'message') {
            const { data: { message, severity } } = packet.payload

            setShowNotification(true)
            setNotificationSeverity(severity)
            setNotificationMessage(message)
        } else if (event === 'busy') {
            const { data: { busy: b } } = packet.payload
            setBusy(!!b)
        }
    }, [])

    React.useEffect(() => {
        const unsubscribe = Hub.listen(notificationChannelName, notificationListener)
        return unsubscribe
    }, [notificationListener])

    return (
        <AppContext.Provider value={{
            user, setUser
        }}>
            <Router>
                <LoadingModal open={checkingLogin || busy} />
                <Navbar
                    signedIn={signedIn || false}
                    user={user}/>
                <NotificationPopUp
                    onClose={() => setShowNotification(false)}
                    show={showNotification}
                    severity={notificationSeverity}
                    message={notificationMessage}/>
                <Switch>
                    <AppRoute type="not-auth" signedIn={signedIn} exact path="/oprette-bruger">
                        <SignUp/>
                    </AppRoute>
                    <AppRoute type="not-auth" signedIn={signedIn} exact path="/bekraeftet">
                        <ConfirmSignUp/>
                    </AppRoute>
                    <AppRoute type="not-auth" signedIn={signedIn} exact path="/log-ind">
                        <Login/>
                    </AppRoute>
                    <AppRoute type="not-auth" signedIn={signedIn} exact path="/gendan-adgangskode">
                        <ForgotPassword/>
                    </AppRoute>
                    <AppRoute type="not-auth" signedIn={signedIn} exact path="/">
                        <LandingPage/>
                    </AppRoute>
                    <Route exact path="/afslutte">
                        <FinalizeSignUp/>
                    </Route>
                    <Route exact path="/video">
                        <Video/>
                    </Route>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/opgaver">
                        <Puzzles/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/spil">
                        <Game/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/spil/:gameId">
                        <Linker/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/start-spil">
                        <GameStarter/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/spilovervagning">
                        <GameMonitoring/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/escape-rooms">
                        <EscapeRooms/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/profil">
                        <Profile/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/opret-ny-escape-room">
                        <EditEscapeRoom isCreate/>
                    </AppRoute>
                    <AppRoute type="auth" signedIn={signedIn} exact path="/redigere">
                        <EditEscapeRoom/>
                    </AppRoute>
                    <Route path="*">
                        <Redirect to="/"/>
                    </Route>
                </Switch>
            </Router>
        </AppContext.Provider>
    )
}
